
import { useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile, UserRole } from '@/types/database.types';
import { toast } from 'sonner';

interface AuthProfileHook {
  profile: UserProfile | null | undefined;
  isLoading: boolean;
  isError: boolean;
  updateProfile: any;
  refetchProfile: () => Promise<void>;
}

const formatProfile = (profileData: any, email?: string | null): UserProfile => {
  return {
    id: profileData.id,
    email: email || profileData.email || undefined,
    first_name: profileData.first_name || undefined,
    last_name: profileData.last_name || undefined,
    role: profileData.role as UserRole,
    created_at: profileData.created_at,
    updated_at: profileData.updated_at,
    status: (profileData.status === "inactive" ? "inactive" : "active") as "active" | "inactive"
  };
};

/**
 * Profile hook that receives userId/email from AuthContext (single source of truth).
 * Uses ONE deterministic query path: direct read → ensureProfile → edge-function fallback.
 * Never returns intermediate null during fallback — all fallback happens within the same queryFn.
 */
export const useAuthProfile = (
  userId: string | null,
  userEmail: string | null
): AuthProfileHook => {
  const queryClient = useQueryClient();
  const isRefetchingRef = useRef(false);

  const refetchProfile = useCallback(async () => {
    if (isRefetchingRef.current) return;
    isRefetchingRef.current = true;
    try {
      await queryClient.refetchQueries({ queryKey: ['profile', userId] });
    } finally {
      isRefetchingRef.current = false;
    }
  }, [queryClient, userId]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['profile', userId],
    queryFn: async (): Promise<UserProfile | null> => {
      if (!userId) return null;

      // Step 1: Direct table read
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (!error && profile) {
        return formatProfile(profile, userEmail);
      }

      const isRlsIssue = error && (
        error.message.includes('infinite recursion') ||
        error.message.includes('policy') ||
        error.message.includes('permission denied') ||
        error.code === '42P17' ||
        error.code === '42501'
      );

      // Schema cache miss — retry will handle it
      const isSchemaCacheMiss = error && (
        error.message.includes('schema cache') ||
        error.code === 'PGRST205'
      );
      if (isSchemaCacheMiss) {
        throw error; // Let react-query retry handle it
      }

      // Step 2: If no profile found (but no error), try ensureProfile
      if (!error && !profile) {
        console.log('No profile row found, creating via ensureProfile');
        try {
          const { data: createData, error: createError } = await supabase.functions.invoke('invite-user', {
            body: { action: 'ensureProfile', userId, email: userEmail }
          });
          if (!createError && createData?.profile) {
            return formatProfile(createData.profile, userEmail);
          }
        } catch (e) {
          console.warn('ensureProfile failed:', e);
        }
      }

      // Step 3: Edge function fallback (for RLS issues or missing profile)
      if (isRlsIssue || (!error && !profile)) {
        console.log('Falling back to edge function for profile fetch');
        const { data: edgeData, error: edgeError } = await supabase.functions.invoke('invite-user', {
          body: { action: 'fetchUserProfile', userId }
        });

        if (!edgeError && edgeData?.profile) {
          return formatProfile(edgeData.profile, userEmail);
        }
      }

      // If we had a non-RLS error, throw it
      if (error && !isRlsIssue) {
        throw error;
      }

      console.error('All profile fetch methods exhausted for user:', userId);
      return null;
    },
    enabled: !!userId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    staleTime: 60000,
    gcTime: 300000,
  });

  const updateProfile = useMutation({
    mutationFn: async (updates: Partial<UserProfile>) => {
      if (!userId) throw new Error("User not authenticated");

      const profileUpdates = { ...updates };
      if ('email' in profileUpdates) delete profileUpdates.email;

      const { data, error } = await supabase
        .from('user_profiles')
        .update(profileUpdates as any)
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        if (error.message.includes('infinite recursion') || error.code === '42P17') {
          const { data: edgeData, error: edgeError } = await supabase.functions.invoke('invite-user', {
            body: { action: 'updateProfile', userId, updates: profileUpdates }
          });
          if (edgeError) throw edgeError;
          return edgeData;
        }
        throw error;
      }
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['profile', userId], formatProfile(data, userEmail));
      toast.success("Profile updated");
    },
    onError: (error) => {
      toast.error(`Failed to update profile: ${error instanceof Error ? error.message : 'Unknown error'}`);
    },
  });

  return {
    profile: data,
    isLoading,
    isError,
    updateProfile,
    refetchProfile,
  };
};
